/**
 * @file frontend/src/components/walkthrough/WalkthroughEngine.js
 * @module walkthrough
 * @component WalkthroughEngine
 *
 * Walkthrough Engine — tooltip + highlight overlay for guided tours.
 *
 * Robustness notes (from tester feedback):
 *   - Tooltip is always clamped to the viewport, with a mobile bottom-sheet
 *     mode (< 640px) so it never falls off the side or off the bottom on a
 *     small screen.
 *   - A floating ✕ button is rendered separately from the tooltip card so
 *     the user can always dismiss even if positioning math goes wrong.
 *   - Scroll, resize, and orientationchange all re-resolve the target — the
 *     highlight follows when the user scrolls, and target re-resolution
 *     handles responsive remounts (mobile↔desktop layout switches).
 *   - If a target selector matches no element (because the UI has shifted),
 *     the engine retries briefly and then falls back to a centered tooltip
 *     with no highlight rather than getting "stuck."
 */
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { X, ChevronRight, ChevronLeft, MousePointer, CheckCircle2 } from 'lucide-react';

const MOBILE_BREAKPOINT = 640;

const getViewport = () => ({
  width: typeof window !== 'undefined' ? window.innerWidth : 1024,
  height: typeof window !== 'undefined' ? window.innerHeight : 768,
});

const WalkthroughEngine = ({ steps, onComplete, onDismiss, tourName, tourKey }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);
  const [viewport, setViewport] = useState(getViewport);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  // Prevent the same click that opened the tour (e.g. a dropdown item) from
  // immediately dismissing it via the backdrop. Backdrop clicks are ignored
  // for the first 300 ms after mount.
  const [backdropReady, setBackdropReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const retryRef = useRef(null);
  const tooltipRef = useRef(null);
  const observerRef = useRef(null);
  const findAndHighlightRef = useRef(null);

  const step = steps[currentStep];
  const isMobile = viewport.width < MOBILE_BREAKPOINT;

  const attachResizeObserver = useCallback((el) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!el) return;
    observerRef.current = new ResizeObserver(() => {
      if (!el.isConnected) {
        observerRef.current?.disconnect();
        setTimeout(() => findAndHighlightRef.current?.(), 50);
        return;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) setTargetRect(rect);
    });
    observerRef.current.observe(el);
  }, []);

  const findAndHighlightTarget = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return true; // No target = center tooltip, still valid
    }
    const selectors = step.target.split(',').map(s => s.trim());
    for (const sel of selectors) {
      try {
        const el = document.querySelector(sel);
        if (el) {
          let rect = el.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            const vh = window.innerHeight;
            if (rect.top >= vh || rect.bottom <= 0) {
              el.scrollIntoView({ block: 'center', behavior: 'auto' });
              rect = el.getBoundingClientRect();
            }
            setTargetRect(rect);
            attachResizeObserver(el);
            return true;
          }
        }
      } catch (e) { /* invalid selector, try next */ }
    }
    setTargetRect(null);
    return false;
  }, [step, attachResizeObserver]);

  // Keep ref pointing at the latest version
  findAndHighlightRef.current = findAndHighlightTarget;

  // Disconnect observer whenever the step changes
  useEffect(() => {
    return () => { if (observerRef.current) observerRef.current.disconnect(); };
  }, [currentStep]);

  // Navigate to the correct page for the current step
  useEffect(() => {
    if (!step) return;

    const targetPath = step.page;
    if (targetPath && location.pathname !== targetPath) {
      setIsNavigating(true);
      navigate(targetPath);
      // Wait for page to load, then retry until element appears
      const timer = setTimeout(() => {
        setIsNavigating(false);
        const found = findAndHighlightTarget();
        if (!found && step.target) {
          let retries = 0;
          retryRef.current = setInterval(() => {
            retries++;
            const success = findAndHighlightTarget();
            if (success || retries > 10) {
              clearInterval(retryRef.current);
            }
          }, 500);
        }
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setIsNavigating(false);
      const found = findAndHighlightTarget();
      if (!found && step.target) {
        let retries = 0;
        retryRef.current = setInterval(() => {
          retries++;
          const success = findAndHighlightTarget();
          if (success || retries > 5) {
            clearInterval(retryRef.current);
          }
        }, 500);
      }
    }
    return () => {
      if (retryRef.current) clearInterval(retryRef.current);
    };
  }, [currentStep, step, location.pathname, navigate, findAndHighlightTarget]);

  // Enable backdrop clicks after a short grace period
  useEffect(() => {
    const t = setTimeout(() => setBackdropReady(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Recompute on resize / orientationchange / scroll. Scroll keeps the
  // highlight + tooltip tracking the target while the user scrolls; resize
  // and orientationchange handle viewport transitions (mobile↔desktop, device
  // rotation) — after each, we also re-resolve the target since responsive
  // layouts may have remounted the element.
  useEffect(() => {
    let rafId = null;
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setViewport(getViewport());
        findAndHighlightTarget();
      });
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      window.removeEventListener('scroll', schedule, { capture: true });
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [findAndHighlightTarget]);

  // Measure the tooltip after it renders so our positioning math has the
  // real height, not a guess. useLayoutEffect runs before the browser paints.
  useLayoutEffect(() => {
    if (!tooltipRef.current) return;
    const el = tooltipRef.current;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setTooltipSize(prev => {
        if (Math.abs(prev.width - r.width) < 1 && Math.abs(prev.height - r.height) < 1) return prev;
        return { width: r.width, height: r.height };
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentStep, isNavigating]);

  const handleNext = () => {
    if (retryRef.current) clearInterval(retryRef.current);
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (retryRef.current) clearInterval(retryRef.current);
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    const completed = JSON.parse(localStorage.getItem('completedTours') || '[]');
    const key = tourKey || tourName;
    if (!completed.includes(key)) {
      completed.push(key);
      localStorage.setItem('completedTours', JSON.stringify(completed));
    }
    onComplete?.();
  };

  const handleDismiss = () => {
    if (retryRef.current) clearInterval(retryRef.current);
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible || !steps.length) return null;

  const progress = ((currentStep + 1) / steps.length) * 100;
  const isLastStep = currentStep === steps.length - 1;

  // Tooltip position — clamps everything to the viewport. On mobile we use a
  // bottom-sheet layout so the tooltip never lands off-screen on narrow
  // devices and the user can always reach the controls.
  const getTooltipStyle = () => {
    const padding = 16;
    const vw = viewport.width;
    const vh = viewport.height;

    if (isMobile) {
      // Bottom sheet: full width, anchored to the bottom, scrollable inside.
      return {
        position: 'fixed',
        left: padding,
        right: padding,
        bottom: padding,
        width: 'auto',
        maxHeight: Math.min(vh - padding * 2, vh * 0.7),
        overflowY: 'auto',
      };
    }

    const tooltipWidth = Math.min(400, vw - padding * 2);
    // Use the measured height if we have it, otherwise reserve generous space
    // so the first paint doesn't collide with viewport edges.
    const tooltipHeight = tooltipSize.height || 320;
    const maxHeight = vh - padding * 2;

    const clamp = (val, min, max) => Math.max(min, Math.min(val, max));

    const centerStyle = {
      position: 'fixed',
      top: clamp((vh - tooltipHeight) / 2, padding, vh - tooltipHeight - padding),
      left: clamp((vw - tooltipWidth) / 2, padding, vw - tooltipWidth - padding),
      width: tooltipWidth,
      maxHeight,
      overflowY: 'auto',
    };

    if (!targetRect || isNavigating) return centerStyle;
    // If target spans most of the viewport (e.g. full-page container), centre.
    if (targetRect.height > vh * 0.6) return centerStyle;

    const pos = step.position || 'bottom';
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const desiredLeft = targetCenterX - tooltipWidth / 2;
    const desiredTop = targetCenterY - tooltipHeight / 2;

    let top;
    let left;

    // Try the requested side; fall back to whatever side has room.
    const fitsBelow = targetRect.bottom + padding + tooltipHeight <= vh - padding;
    const fitsAbove = targetRect.top - padding - tooltipHeight >= padding;
    const fitsRight = targetRect.right + padding + tooltipWidth <= vw - padding;
    const fitsLeft  = targetRect.left - padding - tooltipWidth >= padding;

    let chosen = pos;
    const room = { bottom: fitsBelow, top: fitsAbove, right: fitsRight, left: fitsLeft };
    if (!room[chosen]) {
      chosen = ['bottom', 'top', 'right', 'left'].find(p => room[p]) || chosen;
    }

    if (chosen === 'bottom') {
      top = targetRect.bottom + padding;
      left = desiredLeft;
    } else if (chosen === 'top') {
      top = targetRect.top - padding - tooltipHeight;
      left = desiredLeft;
    } else if (chosen === 'right') {
      top = desiredTop;
      left = targetRect.right + padding;
    } else if (chosen === 'left') {
      top = desiredTop;
      left = targetRect.left - padding - tooltipWidth;
    } else {
      // Nothing fits — center the tooltip
      return centerStyle;
    }

    top = clamp(top, padding, vh - tooltipHeight - padding);
    left = clamp(left, padding, vw - tooltipWidth - padding);

    return {
      position: 'fixed',
      top,
      left,
      width: tooltipWidth,
      maxHeight,
      overflowY: 'auto',
    };
  };

  // Highlight cutout style — clamp the rect to the viewport so when the user
  // scrolls and the target goes off-screen the highlight doesn't blow up the
  // page with a giant inset shadow.
  const getHighlightStyle = () => {
    if (!targetRect) return null;
    const vw = viewport.width;
    const vh = viewport.height;
    const visible = (
      targetRect.bottom > 0 &&
      targetRect.top < vh &&
      targetRect.right > 0 &&
      targetRect.left < vw
    );
    if (!visible) return null;
    return {
      top: targetRect.top - 6,
      left: targetRect.left - 6,
      width: targetRect.width + 12,
      height: targetRect.height + 12,
      boxShadow: '0 0 0 9999px rgba(0,0,0,0.45), 0 0 20px 4px rgba(59,130,246,0.5)',
      border: '3px solid #3b82f6',
      borderRadius: '12px',
      pointerEvents: 'none',
      transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
    };
  };

  const highlightStyle = getHighlightStyle();
  const tooltipStyle = getTooltipStyle();

  return createPortal(
    <div className="walkthrough-overlay">
      {/* Backdrop — only shows when no specific element is highlighted; the
          cutout already darkens the rest of the page via boxShadow. */}
      {!highlightStyle && (
        <div
          className="fixed inset-0 z-[9998]"
          style={{ background: 'rgba(0,0,0,0.45)', pointerEvents: backdropReady ? 'auto' : 'none' }}
          onClick={backdropReady ? handleDismiss : undefined}
        />
      )}

      {/* Highlight cutout */}
      {highlightStyle && !isNavigating && (
        <div className="fixed z-[9999] rounded-lg" style={highlightStyle}>
          <div className="absolute inset-0 rounded-lg animate-ping opacity-20" style={{ border: '2px solid #3b82f6' }} />
        </div>
      )}

      {/* Always-visible floating dismiss — top-right corner, never depends on
          tooltip position so the user can always escape. */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Close tour"
        data-testid="walkthrough-floating-close-btn"
        className="fixed top-4 right-4 z-[10001] inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-slate-700 shadow-lg border border-slate-200 hover:bg-slate-100 transition-colors"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Tooltip */}
      <Card ref={tooltipRef} data-testid="walkthrough-tooltip" className="fixed z-[10000] shadow-2xl border-blue-200 overflow-hidden" style={tooltipStyle}>
        <CardContent className="p-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <div className="flex items-center gap-2 min-w-0">
              <MousePointer className="h-4 w-4 shrink-0" />
              <span className="text-sm font-semibold truncate">{tourName}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className="bg-white/20 text-white border-0 text-xs">{currentStep + 1} of {steps.length}</Badge>
              <button className="hover:bg-white/20 rounded p-0.5 transition" onClick={handleDismiss} data-testid="walkthrough-close-btn" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {isNavigating ? (
              <div className="flex items-center gap-3 py-4">
                <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                <span className="text-sm text-slate-600">Navigating to page...</span>
              </div>
            ) : (
              <>
                <h3 className="text-base font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.content}</p>
                {step.tip && (
                  <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800">💡 <strong>Pro Tip:</strong> {step.tip}</p>
                  </div>
                )}
                {!targetRect && step.target && (
                  <div className="mt-3 p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-xs text-slate-500">
                      We couldn't locate this element on the current page — the layout may have changed. Use Next to continue.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Progress + Actions */}
          <div className="px-4 pb-3 pt-1 border-t border-slate-100">
            <Progress value={progress} className="h-1.5 mb-3" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handlePrev} disabled={currentStep === 0} className="text-xs h-8" data-testid="walkthrough-back-btn">
                  <ChevronLeft className="h-3 w-3 mr-1" /> Back
                </Button>
                <button className="text-xs text-slate-400 hover:text-slate-600" onClick={handleDismiss} data-testid="walkthrough-skip-btn">Skip tour</button>
              </div>
              <Button size="sm" onClick={handleNext} className="bg-blue-600 hover:bg-blue-700 text-xs h-8" disabled={isNavigating} data-testid="walkthrough-next-btn">
                {isLastStep ? (
                  <><CheckCircle2 className="h-3 w-3 mr-1" /> Complete</>
                ) : (
                  <>Next <ChevronRight className="h-3 w-3 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>,
    document.body
  );
};

export default WalkthroughEngine;
